import { useEffect, useState } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { useMutation, useQuery } from "@tanstack/react-query";

import MDBox from "components/MDBox";
import { Success, Error } from "ui-components/Alert";
import { formRender, initialValues, stockAttributesValidation } from "./stock-attributes-schema";
import {
  formRender as detailRender,
  initialValues as detailValues,
  stockAttributesDetailValidation,
} from "./stock-attributes-detail-schema";
import StockAttributesInfo from "./components/StockAttributesInfo";
import { inventoryColumns } from "./components/columns";
import MDTypography from "components/MDTypography";
import { StripedDataGrid } from "layouts/views/components/Grid";
import { useCustomerValue } from "providers/CustomerContext";
import { editStockAttributes } from "services/data.inventory.service";
import StockAttributesDetailInfo from "./components/StockAttributesDetailInfo";
import MDButton from "components/MDButton";
import styled from "@emotion/styled";
import { InboundSaveButton } from "ui-components/Form";
import dayjs from "dayjs";
import { Fieldset } from "ui-components/Form";
import { InboundLayout } from "ui-components/Layout";
import {
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { GridFooterContainer, useGridApiContext } from "@mui/x-data-grid";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { getCommonInventoryList } from "services/data.inventory.service";
import { DeleteButton } from "ui-components/IconButton";
import { StockAttributesCaption } from "../components/Caption";
import { FormTitle } from "ui-components/Form";
import { LoadingDialog } from "ui-components/Alert";
import { LoadingAlert } from "ui-components/Alert";
import { Error_ } from "ui-components/Alert";

const defaultDetailValues = {
  ...detailValues,
  holdPallet: { label: "All pallet", value: "A" },
};

const isCellEditable = (params) => {
  if (params.field === "removeMLN") {
    return params.row.mln !== "<N/A>" && (params.row.newMLN === null || params.row.newMLN === "");
  }
  if (params.field === "removeExpiryDate") {
    return params.row.expiryDate !== null && params.row.newExpiryDate === null;
  }
  if (params.field === "newMLN") {
    return params.row.removeMLN === false;
  }
  if (params.field === "newExpiryDate") {
    return params.row.removeExpiryDate === false;
  }
  return false;
};

const EditableStyledDataGrid = styled(StripedDataGrid)({
  ".custom-editable-header": {
    backgroundColor: "#1A73E8 !important",
    color: "#fff",
    fontWeight: "bold",
    whiteSpace: "normal !important",
  },
  ".MuiDataGrid-columnHeaderTitle": {
    whiteSpace: "normal !important",
    lineHeight: 1,
  },
  ".MuiDataGrid-columnHeader": {
    backgroundColor: "#f0f2f5",
  },
  ".MuiDataGrid-cell": {
    whiteSpace: "normal !important",
    "word-wrap": "break-word !important",
  },
  ".right-align-header": {
    textAlign: "right",
  },
  ".highlight": {
    backgroundColor: "pink !important",
  },
});

const customColumn = inventoryColumns.map((x) => {
  if (x.field === "removeMLN") {
    x.renderCell = (params) => {
      if (params.isEditable === false) {
        return null;
      } else {
        return params.value ? <CheckIcon /> : <CloseIcon />;
      }
    };
  }
  if (x.field === "removeExpiryDate") {
    x.renderCell = (params) => {
      if (params.isEditable === false) {
        return null;
      } else {
        return params.value ? <CheckIcon /> : <CloseIcon />;
      }
    };
  }
  if (x.field === "newExpiryDate") {
    x.renderCell = (params) => {
      if (params.value !== null) {
        const date = new Date(params.value);
        const formattedDate = dayjs(date).format("DD / MM / YYYY");
        return <div>{formattedDate}</div>;
      }
    };
    x.renderEditCell = (params) => <CustomDatePicker {...params} />;
  }
  return x;
});

const CustomDatePicker = (props) => {
  const [selectedDate, setSelectedDate] = useState(props.value ? dayjs(props.value) : null);

  const apiRef = useGridApiContext();

  const handleDateChange = async (date, context) => {
    if (context.validationError == null) {
      setSelectedDate(date);
      await apiRef.current.setEditCellValue({
        id: props.id,
        field: props.field,
        value: date ? date.$d : null,
      });
    } else {
      setSelectedDate(null);
      await apiRef.current.setEditCellValue({
        id: props.id,
        field: props.field,
        value: null,
      });
    }
  };

  return (
    <DatePicker
      value={selectedDate}
      autoFocus
      // disablePast
      onChange={handleDateChange}
      format="DD/MM/YYYY"
      slotProps={{
        field: { clearable: true },
      }}
    />
  );
};

function EditStockAttributes() {
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const { customerCode } = useCustomerValue();
  const errorColumns = [
    {
      field: "id",
      headerName: "LocAvaiID",
      sortable: false,
      align: "center",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "locationID",
      headerName: "Location ID",
      sortable: false,
      align: "left",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "skuCode",
      headerName: "SKU Code",
      sortable: false,
      align: "left",
      flex: 1.8,
      minWidth: 180,
    },
    {
      field: "palletNbr",
      headerName: "Pallet Nbr",
      sortable: false,
      align: "left",
      flex: 1.2,
      minWidth: 112,
    },
    {
      field: "cartonID",
      headerName: "Carton ID",
      sortable: false,
      align: "left",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "mln",
      headerName: "MLN",
      sortable: false,
      align: "left",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "expiryDate",
      headerName: "Expiry Date",
      sortable: false,
      align: "left",
      flex: 1,
      minWidth: 130,
    },
    {
      field: "message",
      headerName: "Message",
      sortable: false,
      align: "left",
      flex: 1.6,
      minWidth: 160,
    },
  ];

  const [detail, setDetail] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loadEnabled, setLoadEnabled] = useState(false);

  const [errorItems, setErrorItems] = useState([]);

  const editStockAttributesMutation = useMutation(editStockAttributes, {
    onSuccess: (data) => {
      setDialog(null);
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      setAlert(<Success data={data} onChange={setAlert} />);
    },
    onError: (data) => {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      const errorData = data.response.data.data;
      if (errorData) {
        setDialog(
          <Dialog open={true} maxWidth={"lg"}>
            <DialogTitle>{data.response.data.message}</DialogTitle>
            <DialogContent>
              <EditableStyledDataGrid
                columns={errorColumns}
                rows={errorData}
                sx={{
                  borderRadius: "0.75rem",
                }}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                }}
                getRowHeight={() => "auto"}
                hideFooterSelectedRowCount={true}
              />
            </DialogContent>

            <DialogActions style={{ display: "flex", justifyContent: "center" }}>
              <MDButton
                color="info"
                style={{ width: "4rem" }}
                onClick={() => {
                  setDialog(null);
                }}
              >
                Ok
              </MDButton>
            </DialogActions>
          </Dialog>
        );
        const remainingDetail = detail.filter((row) => !errorData.some((err) => err.id === row.id));
        setDetail(remainingDetail);

        setErrorItems(errorData);
      }
      setLoadEnabled(true);
      setAlert(<Error data={data} onChange={setAlert} />);
    },
  });

  const defaultValues = {
    ...initialValues,
    customerCode,
  };

  const form = useForm({
    defaultValues: defaultValues,
    values: defaultValues,
    resolver: yupResolver(stockAttributesValidation),
  });

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors, isSubmitting },
    getValues,
    trigger,
    reset,
    setValue,
  } = form;

  const [warehouseCode] = watch(["warehouseCode"]);

  useEffect(() => {
    setDetail([]);
    setInventory([]);
  }, [warehouseCode]);

  const infoProps = {
    control,
    errors,
    formRender,
    customerCode,
    setValue,
  };

  const onSubmit = async () => {
    const isValid = await trigger();
    if (!isValid) {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      return;
    }
    setDialog(<LoadingDialog />)

    const { ...data } = getValues();

    const req = {
      ...data,
      stockAttributesDetails: detail.map((x) => {
        const { expiryDate, newExpiryDate, ...data } = x;
        return {
          expiryDate: expiryDate ? dayjs(expiryDate, "DD / MM / YYYY").format("YYYY-MM-DD") : null,
          newExpiryDate: newExpiryDate ? dayjs(newExpiryDate).format("YYYY-MM-DD") : null,
          ...data,
        };
      }),
    };

    editStockAttributesMutation.mutate(req, {
      onSuccess: () => {
        reset();
      },
    });
  };
  const editStatus = editStockAttributesMutation.status === "loading";
  return (
    <InboundLayout lg={12}>
      {alert}
      {dialog}
      <Card sx={{ height: "100%" }}>
        <MDBox p={3}>
          <form noValidate>
            <StockAttributesInfo {...infoProps} />
          </form>
        </MDBox>
      </Card>
      <MDBox sx={{ margin: "1rem 0" }} />
      <StockAttributesDetail
        customerCode={customerCode}
        warehouseCode={warehouseCode}
        inventory={inventory}
        setInventory={setInventory}
        detail={detail}
        setDetail={setDetail}
        loadEnabled={loadEnabled}
        setLoadEnabled={setLoadEnabled}
        errorItems={errorItems}
        setErrorItems={setErrorItems}
      />
      <InboundSaveButton
        label="submit"
        disabled={editStatus || detail.length === 0}
        url="/inventory/stock-attributes"
        onClick={onSubmit}
      />
    </InboundLayout>
  );
}

const StockAttributesDetail = ({
  customerCode,
  warehouseCode,
  inventory,
  setInventory,
  detail,
  setDetail,
  loadEnabled,
  setLoadEnabled,
  errorItems,
  setErrorItems,
}) => {
  const [alert, setAlert] = useState(null);

  const handleProcessRowUpdateError = (error) => {
    setAlert(<Error content={error.message} onChange={setAlert} />);
  };

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox p={3}>
        <InventoryGrid
          customerCode={customerCode}
          warehouseCode={warehouseCode}
          inventory={inventory}
          setInventory={setInventory}
          detail={detail}
          setDetail={setDetail}
          setAlert={setAlert}
          loadEnabled={loadEnabled}
          setLoadEnabled={setLoadEnabled}
          handleProcessRowUpdateError={handleProcessRowUpdateError}
          errorItems={errorItems}
          setErrorItems={setErrorItems}
        />
        {alert}
        <PreviewGrid
          inventory={inventory}
          setInventory={setInventory}
          detail={detail}
          setDetail={setDetail}
          handleProcessRowUpdateError={handleProcessRowUpdateError}
        />
      </MDBox>
    </Card>
  );
};

const InventoryGrid = ({
  customerCode,
  warehouseCode,
  inventory,
  setInventory,
  detail,
  setDetail,
  setAlert,
  handleProcessRowUpdateError,
  loadEnabled,
  setLoadEnabled,
  errorItems,
  setErrorItems,
}) => {
  const [formValues, setFormValues] = useState({ ...defaultDetailValues });
  const [selectedIds, setSelectedIds] = useState([]);
  const [nMLN, setnMLN] = useState(null);
  const [nExpDate, setNExpDate] = useState(null);

  const form = useForm({
    defaultValues: formValues,
    values: formValues,
    resolver: yupResolver(stockAttributesDetailValidation),
  });
  const {
    control,
    watch,
    formState: { errors, isDirty, isSubmitting },
    handleSubmit,
    reset,
  } = form;

  useEffect(() => {
    reset();
  }, [warehouseCode]);

  const [zoneID, locationID, vendorCode, skuCode, cartonID, palletNbr, holdPallet] = watch([
    "zoneID",
    "locationID",
    "vendorCode",
    "skuCode",
    "cartonID",
    "palletNbr",
    "holdPallet",
  ]);

  const detailProps = {
    control,
    errors,
    formRender: detailRender,
    watch,
    warehouseCode,
  };

  const inventoryReq = {
    warehouseCode,
    customerCode,
    zoneID,
    locationID,
    vendorCode,
    skuCode,
    palletNbr,
    cartonID,
    holdPallet: holdPallet.value,
    type: "StockAttributes",
  };

  const {isFetching} = useQuery({
    queryKey: [
      "stock-attributes-inventory-list",
      warehouseCode,
      customerCode,
      zoneID,
      locationID,
      vendorCode,
      skuCode,
      palletNbr,
      cartonID,
      holdPallet,
      loadEnabled,
    ],
    queryFn: () => getCommonInventoryList(inventoryReq),
    enabled: loadEnabled,
    onSuccess: (data) => {
      const list = data.data.data;
      if (list.length === 0) {
        setAlert(
          <Error
            content="No pallets found, might be occupied by Physical Count CCFreeze, Replenishment, Putaway"
            onChange={setAlert}
          />
        );
        setInventory([]);
      } else {
        const inventoryList = list
          .map((x) => ({
            ...x,
            newMLN: null,
            newExpiryDate: null,
            removeMLN: false,
            removeExpiryDate: false,
          }))
          .filter((x) => !detail.find((y) => x.id === y.id));
        setInventory(inventoryList);
      }
      setLoadEnabled(false);
    },
  });

  const onSubmit = () => {
    setLoadEnabled(true);
  };

  const handleRowUpdate = (updatedRow, originalRow) => {
    if (JSON.stringify(updatedRow) !== JSON.stringify(originalRow)) {
      if (!selectedIds.includes(updatedRow.id)) {
        setSelectedIds([...selectedIds, updatedRow.id]);
      }
      const newInventory = inventory.map((inv) => {
        if (inv.id === updatedRow.id) {
          originalRow.newMLN = updatedRow.newMLN;
          originalRow.newExpiryDate = updatedRow.newExpiryDate;
          inv.newMLN = updatedRow.newMLN;
          inv.newExpiryDate = updatedRow.newExpiryDate;
          originalRow.removeMLN = updatedRow.removeMLN;
          originalRow.removeExpiryDate = updatedRow.removeExpiryDate;
          inv.removeMLN = updatedRow.removeMLN;
          inv.removeExpiryDate = updatedRow.removeExpiryDate;
        }
        return inv;
      });
      setInventory(newInventory);
    }
    return originalRow;
  };

  function deepCopy(obj) {
    if (obj === null || typeof obj !== "object") {
      // If the object is a primitive or null, return it as is
      return obj;
    }

    if (obj instanceof Date) {
      // If the object is a Date, create a new Date with the same value
      return new Date(obj.getTime());
    }

    if (Array.isArray(obj)) {
      // If the object is an array, create a new array and deep copy its elements
      return obj.map((element) => deepCopy(element));
    }

    // If the object is a regular object, create a new object and deep copy its properties
    const copiedObject = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        copiedObject[key] = deepCopy(obj[key]);
      }
    }

    return copiedObject;
  }

  const saveDetail = () => {
    const newDetail = inventory.filter(
      (x) =>
        (x.newMLN !== null && x.newMLN != "") ||
        (x.newExpiryDate !== null && x.newExpiryDate != "") ||
        x.removeMLN === true ||
        x.removeExpiryDate === true
    );
    const unsuccessfulDetail = inventory.filter((x) =>
      selectedIds.some(y => y === x.id) && !newDetail.some(y => y.id === x.id)).map(x => {
        return x.skuCode + ': No changes on MLN nor ExpiryDate'
      });

    if (unsuccessfulDetail.length > 0) {
      const errorMsg = (
        <>
          {unsuccessfulDetail.map((line, index) => (
            <span key={index}>
              {line}
              <br />
            </span>
          ))}
        </>
      );
      setAlert(<Error_ content={errorMsg} onChange={setAlert} />);
      if (newDetail.length === 0) {
        return;
      }
    }
    if (detail.length > 0) {
      let modifiedDetail = deepCopy(detail);

      const filteredInv = newDetail.filter((x) => !detail.some((y) => y.id === x.id));

      modifiedDetail = [...modifiedDetail, ...filteredInv];

      setDetail(modifiedDetail);
    } else {
      setDetail(newDetail);
    }

    setErrorItems([]);

    const leftInventory = inventory.filter(
      (x) =>
        (x.newMLN === null || x.newMLN === "") &&
        (x.newExpiryDate === null || x.newExpiryDate === "") &&
        x.removeMLN === false &&
        x.removeExpiryDate === false
    );
    if (leftInventory.length < inventory.length) {
      setInventory(leftInventory);
    }
  };

  const selectRow = (id) => {
    if (selectedIds.length > id.length) {
      const removed = selectedIds.filter((x) => !id.includes(x));
      let updatedRow = inventory;
      removed.forEach((removedID) => {
        updatedRow = updatedRow.map((x) => {
          if (x.id === removedID) {
            return {
              ...x,
              newMLN: null,
              newExpiryDate: null,
              removeMLN: false,
              removeExpiryDate: false,
            };
          } else return x;
        });
      });
      setInventory(updatedRow);
    } else {
      const insert = id.filter((x) => !selectedIds.includes(x));
      let updatedRow = inventory;
      insert.forEach((insertID) => {
        updatedRow = updatedRow.map((x) => {
          if (x.id === insertID) {
            return {
              ...x,
              newMLN: nMLN,
              newExpiryDate: nExpDate ? dayjs(nExpDate, "DD / MM / YYYY").toDate() : null,
              removeMLN: false,
              removeExpiryDate: false,
            };
          } else return x;
        });
      });
      setInventory(updatedRow);
    }
    setSelectedIds(id);
  };

  const applyAll = () => {
    const updatedRow = inventory.map((x) => {
      if (selectedIds.includes(x.id)) {
        return {
          ...x,
          newMLN: nMLN,
          newExpiryDate: nExpDate ? dayjs(nExpDate, "DD / MM / YYYY").toDate() : null,
        };
      } else return x;
    });
    setInventory(updatedRow);
  };

  return (
    <Fieldset label={"Stock Attributes Detail"}>
      {(isFetching) && <LoadingAlert />}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <StockAttributesDetailInfo {...detailProps} />
        <MDBox mt={2} width="100%" display="flex" justifyContent="center">
          <MDButton
            type="submit"
            variant="gradient"
            color="info"
            disabled={customerCode === null || warehouseCode === null}
          >
            Load
          </MDButton>
        </MDBox>
      </form>
      <FormTitle title="Edit Stock Attributes Detail" />
      <MDBox mt={1.625}>
        <Grid container spacing={3}>
          <Grid item xs={3}>
            <TextField
              label="MLN"
              fullWidth
              value={nMLN}
              onChange={(e) => setnMLN(e.target.value)}
            />
          </Grid>
          <Grid item xs={3}>
            <DatePicker
              value={nExpDate}
              label={"Expiry Date"}
              // disablePast
              onChange={setNExpDate}
              format="DD/MM/YYYY"
              slotProps={{
                field: { clearable: true },
              }}
              sx={{
                width: "100%",
              }}
            />
          </Grid>
          <Grid item>
            <MDButton
              color="info"
              onClick={applyAll}
              disabled={!(Boolean(nMLN) || (Boolean(nExpDate) && nExpDate >= new Date()))}
            >
              Apply All
            </MDButton>
          </Grid>
        </Grid>
      </MDBox>
      <MDTypography mt={1.625} mb={2} color="info" variant="h5">
        Inventory Datagrid Display
      </MDTypography>
      <div style={{ height: 500, width: "100%" }}>
        <EditableStyledDataGrid
          autoHeight={false}
          columns={customColumn}
          rows={inventory}
          checkboxSelection={true}
          disableRowSelectionOnClick={true}
          onRowSelectionModelChange={selectRow}
          rowSelectionModel={selectedIds}
          sx={{
            borderRadius: "0.75rem",
          }}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          getRowHeight={() => "auto"}
          processRowUpdate={handleRowUpdate}
          onProcessRowUpdateError={handleProcessRowUpdateError}
          hideFooterSelectedRowCount={true}
          isCellEditable={isCellEditable}
          getRowClassName={(params) => {
            return errorItems.some((x) => x.id === params.row.id) ? "highlight" : "";
          }}
        />
      </div>
      <MDBox mt={2} width="100%" display="flex" justifyContent="center">
        <MDButton variant="gradient" color="info" onClick={saveDetail}>
          Add
        </MDButton>
      </MDBox>
    </Fieldset>
  );
};

const PreviewGrid = ({
  inventory,
  setInventory,
  detail,
  setDetail,
  handleProcessRowUpdateError,
}) => {
  const addBackInventory = (req) => {
    const { newMLN, newExpiryDate, removeMLN, removeExpiryDate, ...data } = req;
    const updatedInventory = [
      ...inventory,
      { newMLN: null, newExpiryDate: null, removeMLN: false, removeExpiryDate: false, ...data },
    ];
    setInventory(updatedInventory);
  };

  const handleDeleteClick = (req) => {
    addBackInventory(req);
    setDetail(detail.filter((row) => !(row.id === req.id)));
  };
  const previewColumn = [
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 80,
      cellClassName: "actions",
      getActions: ({ row }) => {
        return [
          <DeleteButton
            title="Delete"
            onClick={() => {
              handleDeleteClick(row);
            }}
          />,
        ];
      },
    },
    ...customColumn,
  ];

  const handleRowUpdate = (updatedRow, originalRow) => {
    if (
      !(
        (updatedRow.newMLN !== null && updatedRow.newMLN != "") ||
        (updatedRow.newExpiryDate !== null && updatedRow.newExpiryDate != "") ||
        updatedRow.removeMLN === true ||
        updatedRow.removeExpiryDate === true
      )
    ) {
      handleDeleteClick(updatedRow);
      return { ...originalRow, _action: "delete" };
    } else {
      const newDetail = detail.map((det) => {
        if (det.id === updatedRow.id) {
          originalRow.newMLN = updatedRow.newMLN;
          originalRow.newExpiryDate = updatedRow.newExpiryDate;
          det.newMLN = updatedRow.newMLN;
          det.newExpiryDate = updatedRow.newExpiryDate;
          originalRow.removeMLN = updatedRow.removeMLN;
          originalRow.removeExpiryDate = updatedRow.removeExpiryDate;
          det.removeMLN = updatedRow.removeMLN;
          det.removeExpiryDate = updatedRow.removeExpiryDate;
        }
        return det;
      });
      setDetail(newDetail);
    }
    return originalRow;
  };

  const caption = () => {
    return <StockAttributesCaption totalRows={detail.length} />;
  };

  return (
    <MDBox p={3}>
      <MDTypography mt={1.625} mb={2} color="info" variant="h5">
        Stock Attributes Detail Preview
      </MDTypography>
      <div style={{ height: 500, width: "100%" }}>
        <EditableStyledDataGrid
          autoHeight={false}
          columns={previewColumn}
          rows={detail}
          sx={{
            borderRadius: "0.75rem",
          }}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          getRowHeight={() => "auto"}
          processRowUpdate={handleRowUpdate}
          onProcessRowUpdateError={handleProcessRowUpdateError}
          hideFooterSelectedRowCount={true}
          isCellEditable={isCellEditable}
          slots={{
            footer: caption,
          }}
        />
      </div>
    </MDBox>
  );
};

export default EditStockAttributes;
